import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { ShoppingDataLayerProduct } from '@makro-shopping/use-product-card-logic/dist/types/product.type';
import { ANALYTICS, CONTENT_TYPES, CURRENCY_CODES } from '../constants/analytics';
import {
  BASKET_UPDATE_ITEM_SOURCE_EVENTS,
  BASKET_UPDATE_ITEM_TRIGGER_TYPE,
  SUGGESTEDLIST_CHANNELS,
} from '../constants/analytics/commonAttribute';
import { AS_HALF_PRODUCT_QUANTITY } from '../constants/common';
import SendBrowserEvent from '../services/sendBrowserEvent.service';
import { CarouselAnalyticsProps } from '../types/carousel.types';
import { Product, ProductSearchResult } from '../types/product.types';
import { marketingAnalytics, productAnalytics } from '../utils/analytic/analytic.util';
import { removeProductIdPrefix } from '../utils/common';
import { SOURCE_EVENTS } from '../utils/constants';
import { useBasket } from './useBasket';
import { useCurrentBasketItem } from './useCurrentBasketItem';
import { useSearchData } from './useSearchContext';

const INCREMENT_DECREMENT_DEBOUNCE_INTERVAL = 300;

interface UseCounterProps {
  initialCount?: number;
  enablePartialQuantity?: boolean;
  product: Product | ProductSearchResult | ShoppingDataLayerProduct;
  indexClicked?: number;
  onUpdateBasket?: () => void;
  closePicker?: () => void;
  analyticsProps?: CarouselAnalyticsProps;
  categoryAnalytics?: {
    categoryName?: string;
  };
  isRecommendationCarousel?: boolean;
  triggerCarouselImpressionEvent?: () => void;
  isFromBasket?: boolean;
}

export const useCounter = ({
  initialCount = 0,
  enablePartialQuantity = false,
  product,
  indexClicked,
  onUpdateBasket,
  closePicker,
  analyticsProps,
  categoryAnalytics,
  isRecommendationCarousel,
  triggerCarouselImpressionEvent,
  isFromBasket,
}: UseCounterProps) => {
  const [searchParams] = useSearchParams();
  const { isNative } = Object.fromEntries(searchParams.entries());
  const isNativeView = isNative?.toLocaleLowerCase() === 'true';
  const [count, setCount] = useState<number>(initialCount);
  const [inProgress, setProgress] = useState(false);
  const { query } = useSearchData();
  const { updateBasketItem } = useBasket();
  const loading = false;
  const { displayPrice: price, originPrice } = product;
  const pageNameFromProps = analyticsProps?.pageName ?? '';
  const productIdWithoutPrefix = removeProductIdPrefix(product?.id ?? '');

  const variantId = useMemo(
    () =>
      (product as ProductSearchResult).variantId ||
      (product as Product)?.variants?.[0]?.id ||
      (product as ShoppingDataLayerProduct)?.variants?.[0]?.id ||
      '',
    [product],
  );

  const productForHook = useMemo(() => {
    if ('variants' in product && Array.isArray(product.variants)) {
      return product as Product;
    }
    return product as ProductSearchResult;
  }, [product]);

  const { basketItem, productCount } = useCurrentBasketItem(productForHook);

  const preventDefault = useCallback((e: SyntheticEvent) => {
    e.stopPropagation();
    e.nativeEvent?.stopImmediatePropagation?.();
  }, []);

  const analyticsData = useMemo(
    () => ({
      sourceEvent: '',
      triggerType: '',
      productId: productIdWithoutPrefix || '',
      productPrice: product.displayPrice || '',
      qty: 0,
    }),
    [product, productIdWithoutPrefix],
  );

  const triggerCarouselEvent = useCallback(() => {
    if (isRecommendationCarousel) {
      triggerCarouselImpressionEvent && triggerCarouselImpressionEvent();
    }
  }, [isRecommendationCarousel, triggerCarouselImpressionEvent]);

  useEffect(() => {
    setCount(initialCount);
  }, [initialCount]);

  const incrementDebounced = useMemo(() => {
    return debounce(async (newCount: number) => {
      if (loading) return;

      try {
        setProgress(true);
        onUpdateBasket?.();

        const sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.INCREASE_ITEM;
        const analyticsToSend = {
          ...analyticsData,
          ...(pageNameFromProps && { pageName: pageNameFromProps }),
          qty: newCount,
          triggerType: BASKET_UPDATE_ITEM_TRIGGER_TYPE.BUTTON,
          sourceEvent,
        };

        await updateBasketItem(
          {
            productId: productIdWithoutPrefix,
            variantId,
            quantity: newCount,
            price: price || 0,
            originPrice: originPrice || 0,
            productDetails: product as Product,
          },
          enablePartialQuantity,
          {},
          isFromBasket
        );
        triggerCarouselEvent();
        SendBrowserEvent.triggerAddToCartAnalytics(ANALYTICS.UPDATE_BASKET_ITEM, analyticsToSend, isFromBasket);
        SendBrowserEvent.refreshBasket(isFromBasket);
      } catch {
        setCount(count);
      } finally {
        setProgress(false);
      }
    }, INCREMENT_DECREMENT_DEBOUNCE_INTERVAL);
  }, [
    loading,
    analyticsData,
    variantId,
    product,
    enablePartialQuantity,
    count,
    onUpdateBasket,
    price,
    originPrice,
    pageNameFromProps,
    triggerCarouselEvent,
    updateBasketItem,
    productIdWithoutPrefix,
    isFromBasket
  ]);

  const decrementDebounced = useMemo(() => {
    return debounce(async (newCount: number) => {
      if (loading || newCount === 0) return;

      try {
        setProgress(true);
        onUpdateBasket?.();

        const sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.DECREASE_ITEM;
        const analyticsToSend = {
          ...analyticsData,
          ...(pageNameFromProps && { pageName: pageNameFromProps }),
          qty: newCount,
          triggerType: BASKET_UPDATE_ITEM_TRIGGER_TYPE.BUTTON,
          sourceEvent,
        };

        await updateBasketItem(
          {
            productId: productIdWithoutPrefix,
            variantId,
            quantity: newCount,
            price: price || 0,
            originPrice: originPrice || 0,
            productDetails: product as Product,
          },
          enablePartialQuantity,
          {},
          isFromBasket
        );
        triggerCarouselEvent();
        SendBrowserEvent.triggerAddToCartAnalytics(ANALYTICS.UPDATE_BASKET_ITEM, analyticsToSend, isFromBasket);
        SendBrowserEvent.refreshBasket(isFromBasket);
      } catch {
        setCount(count);
      } finally {
        setProgress(false);
      }
    }, INCREMENT_DECREMENT_DEBOUNCE_INTERVAL);
  }, [
    loading,
    analyticsData,
    variantId,
    product,
    enablePartialQuantity,
    count,
    onUpdateBasket,
    price,
    originPrice,
    pageNameFromProps,
    triggerCarouselEvent,
    updateBasketItem,
    productIdWithoutPrefix,
    isFromBasket
  ]);

  const onDecrement = useCallback(
    (e: SyntheticEvent) => {
      preventDefault(e);
      const decrement = enablePartialQuantity ? AS_HALF_PRODUCT_QUANTITY : 1;
      const newCount = Math.max(0, count - decrement);
      setCount(newCount);
      decrementDebounced(newCount);
    },
    [count, enablePartialQuantity, decrementDebounced, preventDefault],
  );

  const onIncrement = useCallback(
    (e: SyntheticEvent) => {
      preventDefault(e);
      const increment = enablePartialQuantity ? AS_HALF_PRODUCT_QUANTITY : 1;
      const newCount = count + increment;
      setCount(newCount);
      incrementDebounced(newCount);
    },
    [count, enablePartialQuantity, incrementDebounced, preventDefault],
  );

  const onChange = useCallback(
    async (quantity: number, actionType: string): Promise<boolean> => {
      try {
        setCount(quantity);
        setProgress(true);
        onUpdateBasket?.();

        let sourceEvent = null;
        if (quantity > productCount) {
          sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.INCREASE_ITEM;
        } else if (quantity < productCount) {
          sourceEvent =
            quantity === 0
              ? BASKET_UPDATE_ITEM_SOURCE_EVENTS.DELETE_ITEM
              : BASKET_UPDATE_ITEM_SOURCE_EVENTS.DECREASE_ITEM;
        }

        if (!sourceEvent) return true;

        const analyticsToSend = {
          ...analyticsData,
          ...(pageNameFromProps && { pageName: pageNameFromProps }),
          qty: quantity,
          triggerType: actionType,
          sourceEvent,
        };

        await updateBasketItem(
          {
            productId: productIdWithoutPrefix,
            variantId,
            quantity: quantity,
            price: price || 0,
            originPrice: originPrice || 0,
            productDetails: product as Product,
          },
          enablePartialQuantity,
          {},
          isFromBasket
        );
        triggerCarouselEvent();
        SendBrowserEvent.triggerAddToCartAnalytics(ANALYTICS.UPDATE_BASKET_ITEM, analyticsToSend, isFromBasket);
        SendBrowserEvent.refreshBasket(isFromBasket);
        return true;
      } catch (e) {
        console.error(e);
        setCount(basketItem?.quantity || 0);
        return false;
      } finally {
        setProgress(false);
      }
    },
    [
      productCount,
      analyticsData,
      variantId,
      basketItem,
      product,
      enablePartialQuantity,
      onUpdateBasket,
      price,
      originPrice,
      pageNameFromProps,
      triggerCarouselEvent,
      updateBasketItem,
      productIdWithoutPrefix,
      isFromBasket
    ],
  );

  const onDelete = useCallback(
    async (e?: SyntheticEvent) => {
      if (e) preventDefault(e);
      if (!basketItem) return;

      setProgress(true);
      try {
        const analyticsToSend = {
          ...analyticsData,
          ...(pageNameFromProps && { pageName: pageNameFromProps }),
          sourceEvent: BASKET_UPDATE_ITEM_SOURCE_EVENTS.DELETE_ITEM,
          triggerType: BASKET_UPDATE_ITEM_TRIGGER_TYPE.BUTTON,
        };

        await updateBasketItem(
          {
            productId: productIdWithoutPrefix,
            variantId,
            quantity: 0,
            price: price || 0,
            originPrice: originPrice || 0,
            productDetails: product as Product,
          },
          enablePartialQuantity,
          {},
          isFromBasket
        );
        closePicker?.();
        triggerCarouselEvent();
        SendBrowserEvent.triggerAddToCartAnalytics(ANALYTICS.UPDATE_BASKET_ITEM, analyticsToSend, isFromBasket);
        SendBrowserEvent.refreshBasket(isFromBasket);
      } catch (error) {
        console.error('Failed to delete item:', error);
      } finally {
        setProgress(false);
        closePicker?.();
        setCount(0);
      }
    },
    [
      basketItem,
      variantId,
      product,
      analyticsData,
      preventDefault,
      closePicker,
      price,
      originPrice,
      pageNameFromProps,
      triggerCarouselEvent,
      updateBasketItem,
      enablePartialQuantity,
      productIdWithoutPrefix,
      isFromBasket
    ],
  );

  const onAdd = useCallback(
    async (e: SyntheticEvent) => {
      preventDefault(e);
      setCount(1);
      if (loading) return;

      setProgress(true);
      onUpdateBasket?.();

      const appFlyerProperties = {
        af_content_type: CONTENT_TYPES.PRODUCT,
        af_currency: CURRENCY_CODES.THB,
        af_price: product?.displayPrice ?? '',
        af_quantity: 1,
        af_content: JSON.stringify([
          {
            id: productIdWithoutPrefix ?? '',
            quantity: 1,
          },
        ]),
      };

      const analyticsParams = {
        channel: isNativeView ? SUGGESTEDLIST_CHANNELS.MKP_APP : SUGGESTEDLIST_CHANNELS.MKP_WEB,
        categoryName: categoryAnalytics?.categoryName ?? '',
        itemCount: productCount !== 0 ? productCount : 1,
        indexClicked,
        ...analyticsProps,
        ...productAnalytics(product as Product, true),
        ...marketingAnalytics(product as Product),
        ...appFlyerProperties,
        ...analyticsData,
      } as any;
      analyticsParams.sourceEvent = analyticsProps?.sourceEvent ?? SOURCE_EVENTS.HOME_PAGE_TOP_PICKS_RECOMMENDATIONS;
      analyticsParams.query = query || '';

      const storeBasketItemData = {
        productId: product?.id || '',
        sourceEvent: analyticsProps?.sourceEvent || '',
        algorithm: analyticsParams?.algorithm || '',
        isDiversifyResult: analyticsParams?.isDiversifyResult || '',
        channel: isNativeView ? SUGGESTEDLIST_CHANNELS.MKP_APP : SUGGESTEDLIST_CHANNELS.MKP_WEB || '',
        carouselName: analyticsParams?.RecommendationCarouselName || '',
        pageName: analyticsParams?.pageName || '',
      };

      try {
        await updateBasketItem(
          {
            productId: productIdWithoutPrefix,
            variantId,
            quantity: enablePartialQuantity ? AS_HALF_PRODUCT_QUANTITY : 1,
            price: price || 0,
            originPrice: originPrice || 0,
            productDetails: product as Product,
          },
          enablePartialQuantity,
          analyticsParams,
          isFromBasket,
        );
        triggerCarouselEvent();
        SendBrowserEvent.triggerAddToCartAnalytics(ANALYTICS.ADD_TO_BASKET, analyticsParams, isFromBasket);
        SendBrowserEvent.addBasketItemsToLocal(storeBasketItemData, isFromBasket);
        SendBrowserEvent.refreshBasket(isFromBasket);
      } catch (e: any) {
        console.log('Failed to add item to basket: ', e.message);
        setCount(initialCount);
      } finally {
        setProgress(false);
      }
    },
    [
      preventDefault,
      loading,
      product,
      onUpdateBasket,
      categoryAnalytics?.categoryName,
      analyticsProps,
      variantId,
      enablePartialQuantity,
      productIdWithoutPrefix,
      initialCount,
      isNativeView,
      originPrice,
      price,
      query,
      triggerCarouselEvent,
      updateBasketItem,
      analyticsData,
      indexClicked,
      productCount,
      isFromBasket,
    ],
  );


  const onDeleteWithProductId = useCallback(
    async (productId: string) => {
      if (loading) return;

      setProgress(true);
      onUpdateBasket?.();

      try {
        await updateBasketItem(
          {
            productId: product.id,
            variantId,
            quantity: 0,
            price,
            originPrice,
            productDetails: product as Product,
          },
          enablePartialQuantity,
        );

        // Only reset count if deleting the main product
        if (productId === product.id) {
          setCount(0);
        }
        SendBrowserEvent.refreshBasket(isFromBasket);
      } catch (error) {
        console.error('Failed to delete item:', error);
      } finally {
        setProgress(false);
        closePicker?.();
      }
    },
    [
      loading,
      product,
      closePicker,
      onUpdateBasket,
      variantId,
      price,
      originPrice,
      enablePartialQuantity,
      updateBasketItem,
      isFromBasket,
    ],
  );

  const onAddToBasketWithQuantity = useCallback(
    async (quantity: number, productId?: string) => {
      if (loading || quantity <= 0) return;

      setProgress(true);
      onUpdateBasket?.();

      const targetProductId = productId || product.id;
      const isSameProduct = basketItem?.productId === targetProductId;

      try {
        const { displayPrice: price, originPrice } = product;

        // Only update existing item if it's the SAME product
        if (basketItem && productCount > 0 && isSameProduct) {
          const sourceEvent =
            quantity > productCount
              ? BASKET_UPDATE_ITEM_SOURCE_EVENTS.INCREASE_ITEM
              : quantity < productCount
                ? BASKET_UPDATE_ITEM_SOURCE_EVENTS.DECREASE_ITEM
                : null;

          if (sourceEvent) {
            await onChange(quantity, variantId);
          }
        } else {
          // Add as new item (either no existing basket item, or different product)
          await updateBasketItem(
            {
              productId: targetProductId,
              variantId,
              quantity,
              price,
              originPrice,
              productDetails: product as Product,
            },
            enablePartialQuantity,
          );

          const appFlyerProperties = {
            af_content_type: CONTENT_TYPES.PRODUCT,
            af_currency: CURRENCY_CODES.THB,
            af_price: product?.displayPrice ?? '',
            af_quantity: 1,
            af_content: JSON.stringify([
              {
                id: `${removeProductIdPrefix(product?.id ?? '')}` || '',
                quantity: 1,
              },
            ]),
          };

          const analyticsParams = {
            channel: isNativeView ? SUGGESTEDLIST_CHANNELS.MKP_APP : SUGGESTEDLIST_CHANNELS.MKP_WEB,
            unitPrice: product.displayPrice,
            itemCount: quantity,
            ...analyticsProps,
            ...productAnalytics(product as Product, true),
            ...marketingAnalytics(product as Product),
            ...appFlyerProperties,
          } as any;

          analyticsParams.sourceEvent = analyticsProps?.sourceEvent;
          analyticsParams.query = query || '';

          SendBrowserEvent.triggerAddToCartAnalytics(ANALYTICS.ADD_TO_BASKET, analyticsParams);
          SendBrowserEvent.refreshBasket(isFromBasket);
        }

        setCount(quantity);
      } catch (error) {
        console.error('Failed to add to basket:', error);
      } finally {
        setProgress(false);
      }
    },
    [
      loading,
      onUpdateBasket,
      product,
      basketItem,
      productCount,
      variantId,
      enablePartialQuantity,
      analyticsProps,
      isNativeView,
      onChange,
      query,
      updateBasketItem,
      isFromBasket,
    ],
  );

  return {
    count,
    inProgress,
    onAdd,
    onIncrement,
    onDecrement,
    onChange,
    onDelete,
    onDeleteWithProductId,
    onAddToBasketWithQuantity,
  };
};
